
import { Options, Vue, prop } from "vue-class-component";
import ModalShowImage from "@/app/ui/components/modal-show-image/index.vue";
import downloadFile from "@/app/infrastructures/misc/common-library/DownloadFile";
import fileNameFromUrl from "@/app/infrastructures/misc/common-library/FileNameFromUrl";
import fileExtensionFromUrl from "@/app/infrastructures/misc/common-library/FileExtensionFromUrl";
  
// First, import ImagePreview component
import ImagePreview from "@/app/ui/views/pusat-resolusi-v2/components/image-preview.vue";
import { MainAppController } from "../../controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { ErrorForm } from "./entities";
import { VideoUtils } from './entities';

// In the Props class, add any necessary props
class Props {
  modelValue = prop<any>({
    required: true
  });
  title = prop<string>({
    default: "",
    type: String
  });
  maxSize = prop<number>({
    default: 256,
    type: Number
  });
  defaultImage = prop<string>({
    default: "",
    type: String
  });
  customStyle = prop<string>({
    default: "",
    type: String
  });
  accept = prop<string>({
    default: "image/png, image/jpg, image/jpeg, application/pdf",
    type: String
  });
  disabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  icon = prop<string>({
    default: "attachment-v2",
    type: String
  });
  showDeleteIcon = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["upload", "preview", "delete"],
  components: {
    ModalShowImage,
    ImagePreview // Add ImagePreview to components
  }
})
// Add to data properties in the class
// In the class properties, update the type to allow null
export default class FilePicker extends Vue.with(Props) {
  isDragFile = false;
  isType = "";
  showImagePreview = false;
  selectedImage: number | null = 0;
  videoThumbnail = "";
  videoDuration = "";

  declare $refs: {
    file: HTMLFormElement;
  };

  get originalFile() {
    return this.modelValue ? URL.createObjectURL(this.modelValue) : "";
  }

  setDragAndDrop(e: any, value: boolean) {
    this.isDragFile = value;
    e.preventDefault();
  }

  onUpload(file: any) {
    this.errorForm = new ErrorForm();
    const extension = this.getExtension(file.name);

    if (!this.validateFileType(extension)) {
      this.setErrorUnsupportedType();
      return;
    }

    const isVideo =
      file.type === "video/mp4" || file.type === "video/webm" || file.type === "video/quicktime";

    const maxSize = isVideo ? 200 : 5;

    if (file.size > maxSize * 1024 * 1024) {
      this.setErrorMaxSize(maxSize);
      return;
    }


    this.$emit("update:modelValue", file);
    // Create blob URL for download
    this.isDragFile = false;
  }

  validateFileType(type: string) {
    // Map of file extensions to their MIME types
    const extensionToMime = {
      png: ["image/png"],
      jpg: ["image/jpg"],
      jpeg: ["image/jpeg"],
      pdf: ["application/pdf"],
      doc: ["application/doc", "application/ms-doc", "application/msword"],
      docx: [
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ],
      xls: [
        "application/excel",
        "application/vnd.ms-excel",
        "application/x-excel",
        "application/x-msexcel"
      ],
      xlsx: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ],
      csv: ["text/csv"],
      mp4: ["video/mp4"],
      mov: ["video/quicktime"],
      webm: ["video/webm"]
    };

    // Get all accepted MIME types
    const acceptedMimes = this.accept
      .split(",")
      .map((mime: string) => mime.trim());

    // Get valid MIME types for the given extension
    const validMimes =
      extensionToMime[type as keyof typeof extensionToMime] || [];

    // Check if any of the valid MIME types for this extension are accepted
    return validMimes.some((mime: string) => acceptedMimes.includes(mime));
  }

  onSubmitClick(e: any) {
    if (!e.target.files?.length) {
      this.setDefault();
      return;
    }

    const file = e.target.files[0];
    this.onUpload(file);
  }

  onSubmitDrag(e: any) {
    if (!e.target.files?.length) {
      this.setDefault();
      return;
    }

    const file = e.dataTransfer && e.dataTransfer.files[0];
    this.onUpload(file);
  }

  setDefault(e?: any) {
    e?.preventDefault();
    e?.stopPropagation();
    this.$emit("update:modelValue", "");

    const refValue = this.$refs?.file;
    if (refValue) {
      refValue.value = "";
    }
    this.isDragFile = false;
  }

  onPreview() {
    this.$emit(
      "preview",
      this.originalFile || this.defaultImage
    );
  }

  getExtension(fileName: string): string {
    return (
      fileName
        ?.split(".")
        ?.pop()
        ?.toLowerCase() || ""
    );
  }
  get images(): string[] {
    if (Array.isArray(this.modelValue)) {
      return this.modelValue.map(file => this.getImageUrl(file));
    }
    return [this.getImageUrl(this.modelValue)];
  }

  getImageUrl(file: any): string {
    if (file instanceof File) {
      if (VideoUtils.isVideoFile(file.name)) {
        return this.videoThumbnail || URL.createObjectURL(file);
      }
      return URL.createObjectURL(file);
    }
    return file?.url || file;
  }

  get imageNames(): string[] {
    if (Array.isArray(this.modelValue)) {
      return this.modelValue.map(file => file.name || 'Image');
    }
    return [this.modelValue?.name || 'Image'];
  }

  downloadFile(fileOrUrl: string | File): void {
    if (fileOrUrl instanceof File) {
      const url = URL.createObjectURL(fileOrUrl);
      downloadFile(url, fileOrUrl.name, "");
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }
  // Helper for default preview file data
  getDefaultPreviewFileData() {
    return {
      type: "",
      color: "",
      actionLabel: "Lihat file",
      action: () => this.onActionFile("image", 0)
    };
  }

  // Helper for video preview file data
  getVideoPreviewFileData(type: string) {
    VideoUtils.generateVideoThumbnail(this.modelValue).then(thumbnail => {
      this.videoThumbnail = thumbnail;
    });
    VideoUtils.getVideoDuration(this.modelValue).then(duration => {
      const minutes = Math.floor(duration / 60);
      const seconds = Math.floor(duration % 60);
      this.videoDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });

    return {
      type,
      color: "#4B5563",
      actionLabel: "Download file",
      action: () => this.downloadFile(this.modelValue),
      isVideo: true
    };
  }

  // Helper for document preview file data
  getDocumentPreviewFileData(type: string, extension: string, actionLabel: string) {
    const file = this.modelValue.url || this.modelValue;
    switch (extension) {
      case "pdf":
        return {
          type,
          color: "#AD0C00",
          actionLabel,
          action: () => this.downloadFile(file)
        };
      case "doc":
      case "docx":
        return {
          type,
          color: "#155EEF",
          actionLabel,
          action: () => this.downloadFile(file)
        };
      case "csv":
        return {
          type,
          color: "#EDEDED",
          text: "#626262",
          actionLabel,
          action: () => this.downloadFile(file)
        };
      case "xls":
        return {
          type,
          color: "#079455",
          actionLabel,
          action: () => this.downloadFile(file)
        };
      case "xlsx":
        return {
          type,
          color: "#016F3E",
          actionLabel,
          action: () => this.downloadFile(file)
        };
      default:
        return this.getDefaultPreviewFileData();
    }
  }

  // Refactored previewFileData getter
  get previewFileData() {
    if (!this.modelValue) return this.getDefaultPreviewFileData();

    const extension = this.getExtension(this.modelValue.name);
    const type = extension.toUpperCase();
    const actionLabel = "Download file";

    if (VideoUtils.isVideoFile(this.modelValue.name)) {
      return this.getVideoPreviewFileData(type);
    }

    return this.getDocumentPreviewFileData(type, extension, actionLabel);
  }

  onActionFile(type = "", index?: number) {
    if (!this.modelValue) return;

    if (!type) {
      try {
        MainAppController.showLoading();

        // Create and configure download link
        const link = document.createElement("a");
        link.href = this.originalFile || this.videoThumbnail;
        link.download = this.modelValue.name;
        link.style.display = "none";

        // Add to DOM, trigger download and cleanup
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(this.originalFile);
        return;

        // Handle image preview
      } catch (error) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Gagal download file!")
        );
      } finally {
        MainAppController.closeLoading();
      }
    }

    this.$emit("preview", this.originalFile || this.defaultImage);
    this.showImagePreview = true;
    this.selectedImage = index ?? 0;
  }

  onReset() {
    this.isDragFile = false;
    this.setDefault();
    this.errorForm = new ErrorForm();
    this.$emit("reset");
  }

  mounted() {
    this.onReset();
  }

  closeImagePreview(): void {
    this.showImagePreview = false;
    this.selectedImage = 0;
  }

  get previewUrl(): string {
    if (this.modelValue instanceof File) {
      return URL.createObjectURL(this.modelValue);
    }
    return this.modelValue || this.defaultImage;
  }

  // error
  errorForm = new ErrorForm();
  setErrorMaxSize(max: number) {
    this.errorForm.maxSize = `Maks. ${max}MB`;
    this.isDragFile = false;
  }
  setErrorUnsupportedType() {
    this.errorForm.unsupportedType = "File tidak sesuai";
    this.setDefault();
  }

  get erroreMessage(): string {
    const { maxSize, unsupportedType } = this.errorForm;
    return unsupportedType || maxSize;
  }

  onDeleteImage() {
    this.$emit("delete");
  }
} // End of class
